/**
 * CheTuo Web — App root with routing.
 *
 * Routes:
 * - /shop-admin/login → LoginPage (门店管理员登录)
 * - /shop-admin/* → Layout (protected) with nested routes
 * - /admin/login → AdminLoginPage (平台超级管理员登录)
 * - /admin/* → AdminLayout (super admin only) with nested routes
 */

import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { AdminLayout } from './components/AdminLayout';
import { LoginPage } from './pages/login/LoginPage';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { DashboardPage as ShopDashboard } from './pages/dashboard/DashboardPage';
import { OrdersPage } from './pages/orders/OrdersPage';
import { OrderDetailPage } from './pages/orders/OrderDetailPage';
import { OrderCreatePage } from './pages/orders/OrderCreatePage';
import { CustomersPage } from './pages/customers/CustomersPage';
import { SettingsPage as ShopSettings } from './pages/settings/SettingsPage';
import { InventoryPage } from './pages/inventory/InventoryPage';
import { PaymentFlowPage } from './pages/payment-flow/PaymentFlowPage';
import { StaffPage } from './pages/staff/StaffPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { DashboardPage as AdminDashboard } from './pages/admin/DashboardPage';
import { ShopsPage } from './pages/admin/ShopsPage';
import { SubscriptionsPage } from './pages/admin/SubscriptionsPage';
import { PaymentOrdersPage } from './pages/admin/PaymentOrdersPage';
import { AiQuotaPage } from './pages/admin/AiQuotaPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { SettingsPage as AdminSettings } from './pages/admin/SettingsPage';
import { AuditLogsPage } from './pages/admin/AuditLogsPage';
import { PlanOrdersPage } from './pages/admin/PlanOrdersPage';
import { useAuth } from './hooks/useAuth';
import { tokenStorage } from './api/client';

function App(): React.JSX.Element {
  const { restore, isLoggedIn, user } = useAuth();

  useEffect(() => {
    restore();
    // Auto-select first shop if not set
    if (!tokenStorage.getShopId()) {
      // Will be set after first shops fetch in dashboard
    }
  }, [restore]);

  return (
    <Routes>
      <Route path="/shop-admin/login" element={<LoginPage />} />
      <Route
        path="/shop-admin"
        element={isLoggedIn ? <Layout /> : <Navigate to="/shop-admin/login" replace />}
      >
        <Route index element={<Navigate to="/shop-admin/dashboard" replace />} />
        <Route path="dashboard" element={<ShopDashboard />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/new" element={<OrderCreatePage />} />
        <Route path="orders/:orderId" element={<OrderDetailPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="payment-flow" element={<PaymentFlowPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="settings" element={<ShopSettings />} />
      </Route>
      <Route
        path="/admin/login"
        element={
          isLoggedIn && user?.isSuperAdmin ? (
            <Navigate to="/admin/dashboard" replace />
          ) : isLoggedIn ? (
            <Navigate to="/shop-admin/dashboard" replace />
          ) : (
            <AdminLoginPage />
          )
        }
      />
      <Route
        path="/admin"
        element={
          isLoggedIn && user?.isSuperAdmin ? (
            <AdminLayout />
          ) : isLoggedIn ? (
            <Navigate to="/shop-admin/dashboard" replace />
          ) : (
            <Navigate to="/admin/login" replace />
          )
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="shops" element={<ShopsPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="payment-orders" element={<PaymentOrdersPage />} />
        <Route path="plan-orders" element={<PlanOrdersPage />} />
        <Route path="ai-quota" element={<AiQuotaPage />} />
        <Route path="admin-users" element={<AdminUsersPage />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="audit-logs" element={<AuditLogsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/shop-admin/dashboard" replace />} />
    </Routes>
  );
}

export default App;
